import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { VehicleTrip, IVehicleTripCoordinate, VehicleTripRuleset } from '@clearroadlab/cam';
import {
  Zone, tripProcessingMode, ITripIntersectionZones, ITripIntersectionZone, ITripDistanceZones, Ruleset
} from '@clearroadlab/geoprocessing';

import { environment } from '../../environments/environment';
import { UsersService } from '../users/users.service';
import { filterParams, handleAPIError, ISearchParams } from '../utils';

const path = `${environment.camApi.baseUrl}/vehicle-trips`;
const rulesetsUrl = `${environment.geoprocessingApi.baseUrl}/rulesets`;
const zonesUrl = `${environment.geoprocessingApi.baseUrl}/zones`;

const vehiclePath = (vin: string) => `${path}/${vin}`;
const vehicleTripPath = (vin: string, tripId: string) => `${vehiclePath(vin)}/${tripId}`;

export class VehicleTripExtended extends VehicleTrip {
  coordinates?: IVehicleTripCoordinate[];
  distanceZones?: ITripDistanceZones[];
  intersectionZones?: ITripIntersectionZones[];
}

export class VehicleTripData extends VehicleTrip {
  processing = false;
  downloading = false;
  coordinates?: IVehicleTripCoordinate[];
  cordonRuleset?: VehicleTripRuleset;
  intersectionZones?: ITripIntersectionZones;
  intersectionRuleset?: VehicleTripRuleset;
}

export class VehicleTripSearchResult {
  count = 0;
  results: VehicleTripData[];
}

export interface IVehicleTripSearchParams extends ISearchParams {
  from?: string;
  to?: string;
}

@Injectable({
  providedIn: 'root'
})
export class VehicleTripsService {
  constructor(
    private http: HttpClient,
    private usersService: UsersService
  ) { }

  async list(vin?: string, params: IVehicleTripSearchParams = {}) {
    return this.http.get<VehicleTripSearchResult>(vin ? vehiclePath(vin) : path, {
      headers: this.usersService.headers,
      params: filterParams({
        ...params,
        admin: true
      })
    }).toPromise().catch(handleAPIError);
  }

  async get(vin: string, tripId: string) {
    return this.http.get<VehicleTrip>(vehicleTripPath(vin, tripId), {
      headers: this.usersService.headers,
      params: { admin: true }
    }).toPromise().catch(handleAPIError);
  }

  async confirm(vin: string, tripId: string) {
    return this.http.put<VehicleTrip>(`${vehicleTripPath(vin, tripId)}/confirm`, {}, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async reject(vin: string, tripId: string, reason: string) {
    return this.http.put<VehicleTrip>(`${vehicleTripPath(vin, tripId)}/reject`, { reason }, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async refreshPricing({ vin, tripId }: VehicleTrip) {
    return this.http.put<VehicleTrip>(`${vehicleTripPath(vin, tripId)}/pricing`, {}, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
  }

  async zones() {
    return this.http.get<Zone[]>(zonesUrl, {
      params: {
        limit: '999999'
      }
    }).toPromise().catch(handleAPIError);
  }

  async cordonZoneIds(cordonKey: string) {
    // TODO check if result exists before unboxing it
    const { zoneIdToRuleId } = await this.http.get<Ruleset>(`${rulesetsUrl}/${cordonKey}`, {
      headers: this.usersService.headers
    }).toPromise().catch(handleAPIError);
    // sort the zone by the ruelId to have the most important one first
    return Object.keys(zoneIdToRuleId).sort((a, b) => zoneIdToRuleId[a] - zoneIdToRuleId[b]);
  }

  async fetchCoordinates({ coordinatesFileUrl }: VehicleTrip) {
    if (!coordinatesFileUrl) {
      return [];
    }
    const { coordinates } = await this.http.get<{
      coordinates: IVehicleTripCoordinate[];
    }>(coordinatesFileUrl).toPromise().catch(() => ({ coordinates: [] }));
    return coordinates;
  }

  async fetchCoordinatesForMap(trip: VehicleTripExtended) {
    if (trip.coordinates && trip.coordinates.length) {
      return;
    }
    const { coordinates } = await this.http.get<{
      coordinates: IVehicleTripCoordinate[];
    }>(trip.coordinatesFileUrl).toPromise().catch(() => ({ coordinates: [] }));
    trip.coordinates = coordinates;
    return trip;
  }
  private async downloadProcessing<T>({ rulesets }: VehicleTrip, processingMode: tripProcessingMode) {
    const empty = {} as T;
    const results = await Promise.all(rulesets.map(async ruleset => {
      const url = ruleset[`${processingMode}FileUrl`];
      return url ? (await this.http.get<{ zones: T }>(url).toPromise().catch(() => ({ zones: {} }))).zones as T : empty;
    }));
    return results.reduce((prev, curr) => ({ ...prev, ...curr }), empty);
  }

  public async fetchDistance(trip: VehicleTrip) {
    return this.downloadProcessing<ITripDistanceZones>(trip, 'distance');
  }

  public async fetchIntersections(trip: VehicleTrip) {
    return this.downloadProcessing<ITripIntersectionZones>(trip, 'intersections');
  }
}
